import { removeEmailFromDisplayName, extractEmailFromDisplayName } from '../utils/emailInDisplayName.js';

export default class UsersMappingService {
    constructor(http) {
        this.http = http;
    }

    getData() {
        return new Promise((resolve, reject) => {
            this.http.get(this.backendUrl + '/mappings/users').then(
                (response) => {
                    if (response.status === 200 && response.data) {
                        const shapedData = this.shapeDataFromWebService(response.data);
                        resolve({
                            ...shapedData, 
                            bulkMappingByEmailAvailable: response.data.BulkMappingByEmailAvailable
                        });
                    }
                },
                (err) => {
                    reject(err);
                }
            );
        });
    }

    async getJiraValues(query, filterMappedUsers) {
        const response = await this.http.get(this.backendUrl + `/mappings/users/jira?query=${query}&filterMappedUsers=${filterMappedUsers}`)
        if (response.status === 200 && response.data) {
            const data = this.shapeDataFromWebService(
                {
                    JiraUsers: response.data,
                    AzureUsers: [],
                    Mappings: [],
                }
            )
            return data;
        };
    }

    createItem(kind, key, value, email) {
        return {
            id: key + '_' + kind,
            kind: kind,
            key: key,
            email: email,
            label: value,
            visible: true
        }
    }

    shapeDataFromWebService(responseData) {
        let items = {};
        Array.prototype.forEach.call(responseData.JiraUsers, el => {
            let item = this.createItem('JIRA', el.Key, el.DisplayName, el.Email);
            items[item.id] = item;
        });

        Array.prototype.forEach.call(responseData.AzureUsers, el => {
            let item = this.createItem('TFS', el.Key, el.DisplayName, el.Email);
            items[item.id] = item;
        });
        return this.hideListItemsAndEnrichMappings(items, responseData.Mappings);
    }

    hideListItemsAndEnrichMappings(availableValues, mappings) {
        Object.values(availableValues).forEach(item => {
            item.visible = true;
        });

        mappings.forEach(mapping => {
            Object.values(availableValues).forEach(item => {
                if (item.kind === 'JIRA' && item.key === mapping.JiraUserToMap.Key) {
                    item.visible = false;
                }
                if (item.kind === 'TFS' && item.key === mapping.TfsUserToMap.Key) {
                    item.visible = false;
                }
            });
            mapping.jiraTitle = removeEmailFromDisplayName(mapping.JiraUserToMap.DisplayName);
            mapping.jiraSubtitle = extractEmailFromDisplayName(mapping.JiraUserToMap.DisplayName);
            mapping.tfsTitle = removeEmailFromDisplayName(mapping.TfsUserToMap.DisplayName);
            let tfsSubtitle = extractEmailFromDisplayName(mapping.TfsUserToMap.DisplayName);
            if (!tfsSubtitle) {
                tfsSubtitle = Object.values(availableValues).find((item) => item.kind === 'TFS' && item.key === mapping.TfsUserToMap.Key)?.email;
            }
            mapping.tfsSubtitle = tfsSubtitle;
            mapping.duringProcessing = false;
        });
        return {
            available: availableValues,
            mappings: mappings
        };
    }

    mapByEmail(availableValues) {
        return new Promise((resolve, reject) => {
            this.http.post(this.backendUrl + '/mappings/users/attempt-map-by-email').then(
                (response) => {
                    if (response.status === 200 && response.data) {
                        const mappedUsersCounter = response.data.MappedUsersCounter
                        const enrichMappingsResult = this.hideListItemsAndEnrichMappings(availableValues, response.data.Mappings);
                        resolve({
                            available: enrichMappingsResult.available,
                            mappings: enrichMappingsResult.mappings,
                            mappedUsersCounter: mappedUsersCounter
                        });
                    }
                },
                (err) => {
                    reject(err);
                }
            )
        });
    }

    addMapping(jiraValue, tfsValue, availableValues, mappings) {
        return new Promise((resolve, reject) => {
            this.http.post(this.backendUrl + '/mappings/users', {
                JiraUserToMap: {
                    Key: jiraValue.key,
                    DisplayName: this.tryToEnrichWithEmail(jiraValue.label, jiraValue.email)
                },
                TfsUserToMap: {
                    Key: tfsValue.key,
                    DisplayName: this.tryToEnrichWithEmail(tfsValue.label, tfsValue.email)
                }
            }).then(
                (response) => {
                    if (response.status === 200 && response.data) {
                        mappings.push(response.data);
                        resolve(this.hideListItemsAndEnrichMappings(availableValues, mappings));
                    }
                },
                (err) => {
                    reject(err);
                }
            )
        });
    }

    removeMapping(availableValues, mappings, mappingObject) {
        return new Promise((resolve, reject) => {
            this.http.delete(this.backendUrl + '/mappings/users/' + mappingObject.Id).then(
                (response) => {
                    if (response.status === 200 && response.data === true) {
                        mappings = mappings.filter(mapping => mapping.Id !== mappingObject.Id);
                        resolve(this.hideListItemsAndEnrichMappings(availableValues, mappings));
                    }
                },
                (err) => {
                    reject(err);
                }
            )
        });
    }

    getDefaultValues() {
        return new Promise((resolve, reject) => {
            this.http.get(this.backendUrl + '/mappings/users/defaults').then(
                (response) => {
                    if (response.status === 200 && response.data) {
                        resolve(this.wrapDefaultValues(response.data));
                    }
                },
                (err) => {
                    reject(err);
                }
            );
        });
    }

    wrapDefaultValues(responseData) {
        let defaultValues = responseData.DefaultValues;

        const createDefaultValue = (value) => {
            return {
                JiraValue: this.createItem('JIRA', value.JiraUserToMap.Key, value.JiraUserToMap.DisplayName, value.JiraUserToMap.Email),
                TfsValue: this.createItem('TFS', value.TfsUserToMap.Key, value.TfsUserToMap.DisplayName, value.TfsUserToMap.Email)
            }
        }

        let items = [];
        Array.prototype.forEach.call(responseData.SpecialValues, el => {
            let jiraItem = this.createItem('JIRA', el.Key, el.Value, el.Email);
            items.push(jiraItem);
            let tfsItem = this.createItem('TFS', el.Key, el.Value, el.Email);
            items.push(tfsItem);
        });

        return {
            DefaultValues: createDefaultValue(defaultValues),
            SpecialValues: items
        }
    }

    updateDefaultValues(defaultValues) {
        return new Promise((resolve, reject) => {
            this.http.put(this.backendUrl + '/mappings/users/defaults', {
                JiraUserToMap: {
                    Key: defaultValues.JiraUserKey,
                    DisplayName: this.tryToEnrichWithEmail(defaultValues.JiraUserValue, defaultValues.jiraEmail)
                },
                TfsUserToMap: {
                    Key: defaultValues.TfsUserKey,
                    DisplayName: this.tryToEnrichWithEmail(defaultValues.TfsUserValue, defaultValues.tfsEmail)
                }
            }).then(
                (response) => {
                    if (response.status === 204) {
                        resolve();
                    }
                },
                (err) => {
                    reject(err);
                }
            );
        });
    }

    tryToEnrichWithEmail(value, email) {
        if (this.includesEmail(value)) return value;
        if (!email) return value;
        return `${value} <${email}>`;
    }

    includesEmail(value) {
        return value !== removeEmailFromDisplayName(value);
    }
}


// WEBPACK FOOTER //
// ./src/service/usersMapping.js